import React, { useState, useEffect } from 'react';
import { MenuIcon, BellIcon } from './icons';
import notificationService from '../services/notificationService.js';
import LogoComponent from '../../shared/LogoComponent.js';
import { useTheme } from '../contexts/ThemeContext';
import { sendBusinessPlanToTelegram } from '../services/businessPlanTelegramService.js';

const BUSINESS_PLAN_STORAGE_KEY = 'busin.us-business-plan';

const Header = ({ 
  onGenerate, 
  isLoading, 
  onOpenNotifications,
  // Workspace props
  workspaces = [],
  currentWorkspace = null,
  onCreateNewWorkspace,
  onSwitchWorkspace,
  onDeleteWorkspace,
  onImportToNewWorkspace,
  // Add nodes, edges, groups for Telegram export
  nodes = [],
  edges = [],
  groups = [],
  // Business plan text to share with sidebar
  businessPlanText = '', // Provide default value
  setBusinessPlanText = () => {} // Provide default function
}) => {
  const [prompt, setPrompt] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [showWorkspaceMenu, setShowWorkspaceMenu] = useState(false);
  const [showNewWorkspaceForm, setShowNewWorkspaceForm] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const { currentTheme, setTheme, themes } = useTheme();

  // Load business plan text from localStorage on mount
  useEffect(() => {
    try {
      const savedBusinessPlan = localStorage.getItem(BUSINESS_PLAN_STORAGE_KEY);
      if (savedBusinessPlan) {
        setBusinessPlanText(savedBusinessPlan);
        setPrompt(savedBusinessPlan);
      }
    } catch (e) {
      console.error("Failed to load business plan from localStorage", e);
    }
  }, [setBusinessPlanText]);

  // Update prompt when businessPlanText changes
  useEffect(() => {
    setPrompt(businessPlanText || ''); // Ensure we have a string
  }, [businessPlanText]);

  useEffect(() => {
    // Subscribe to notification updates to show unread count
    const listener = (notifications) => {
      const count = notifications.filter(n => !n.read).length;
      setUnreadCount(count);
    };
    
    notificationService.addListener(listener);
    
    // Get initial unread count
    setUnreadCount(notificationService.getUnreadCount());
    
    return () => {
      notificationService.removeListener(listener);
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    const promptValue = prompt || ''; // Ensure we have a string
    if (promptValue.trim() && !isLoading) {
      // Save business plan text to localStorage
      try {
        localStorage.setItem(BUSINESS_PLAN_STORAGE_KEY, promptValue);
      } catch (e) {
        console.error("Failed to save business plan to localStorage", e);
      }
      
      // Update shared business plan text
      setBusinessPlanText(promptValue);
      
      // Generate workflow
      onGenerate(promptValue);
    }
  };

  const handleCreateWorkspace = () => {
    const newWorkspaceNameValue = newWorkspaceName || ''; // Ensure we have a string
    if (newWorkspaceNameValue.trim()) {
      onCreateNewWorkspace(newWorkspaceNameValue.trim());
      setNewWorkspaceName('');
      setShowNewWorkspaceForm(false);
      setShowWorkspaceMenu(false);
    }
  };

    return (
      <header className="bg-dark-surface border-b border-dark-border p-4 flex items-center justify-between relative">
        <div className="flex items-center gap-4">
          <button className="p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg sm:hidden">
            <MenuIcon className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <LogoComponent size="md" includeAudio={true} /> 
            <h1 className="text-xl font-bold text-dark-text-primary">Workflow AI</h1>
          </div>
          
          {/* Workspace selector */}
          <div className="relative">
            <button 
              onClick={() => setShowWorkspaceMenu(!showWorkspaceMenu)}
              className="flex items-center gap-2 px-3 py-1.5 bg-dark-bg border border-dark-border rounded-md text-dark-text-primary hover:bg-dark-surface"
            >
              <span className="max-w-[120px] truncate">
                {currentWorkspace?.name || 'Select Workspace'}
              </span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {showWorkspaceMenu && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-dark-surface border border-dark-border rounded-md shadow-lg z-50">
                <div className="py-1">
                  {workspaces.map(workspace => (
                    <button
                      key={workspace.id}
                      onClick={() => {
                        onSwitchWorkspace(workspace.id);
                        setShowWorkspaceMenu(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm flex items-center justify-between ${
                        currentWorkspace?.id === workspace.id 
                          ? 'bg-brand-primary/20 text-brand-primary' 
                          : 'text-dark-text-primary hover:bg-dark-bg'
                      }`}
                    >
                      <span className="truncate">{workspace.name}</span>
                      {workspaces.length > 1 && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteWorkspace(workspace.id);
                          }}
                          className="text-red-400 hover:text-red-300"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </button>
                  ))}
                  
                  <div className="border-t border-dark-border my-1"></div>
                  
                  {showNewWorkspaceForm ? (
                    <div className="px-4 py-2">
                      <input
                        type="text"
                        value={newWorkspaceName || ''} // Ensure we have a string
                        onChange={(e) => setNewWorkspaceName(e.target.value)}
                        placeholder="Workspace name"
                        className="w-full bg-dark-bg border border-dark-border rounded-md px-3 py-1.5 text-sm text-dark-text-primary mb-2"
                        autoFocus
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleCreateWorkspace}
                          className="flex-1 bg-brand-primary text-white rounded-md px-3 py-1.5 text-sm"
                        >
                          Create
                        </button>
                        <button
                          onClick={() => {
                            setShowNewWorkspaceForm(false);
                            setNewWorkspaceName('');
                          }}
                          className="flex-1 bg-dark-bg border border-dark-border text-dark-text-primary rounded-md px-3 py-1.5 text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowNewWorkspaceForm(true)}
                      className="w-full text-left px-4 py-2 text-sm text-dark-text-primary hover:bg-dark-bg flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      New Workspace
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 max-w-2xl mx-4">
          <div className="relative">
            <input
              type="text"
              value={prompt || ''} // Ensure we have a string
              onChange={(e) => {
                const value = e.target.value || ''; // Ensure we have a string
                setPrompt(value);
                setBusinessPlanText(value);
              }}
              placeholder="Describe your business plan..."
              disabled={isLoading}
              className="w-full bg-dark-bg border border-dark-border rounded-lg py-2 px-4 text-dark-text-primary focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:opacity-50"
            />
            <button
              type="submit"
              disabled={!prompt || !prompt.trim() || isLoading} // Added null check
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-brand-primary text-white rounded-md px-4 py-1 text-sm hover:bg-brand-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Generating...' : 'Generate'}
            </button>
          </div>
        </form>
        
        <div className="flex items-center gap-2">
          {/* Theme Selector Button */}
          <div className="relative">
            <button 
              className="p-2 rounded-lg bg-dark-surface border border-dark-border text-dark-text-primary hover:bg-dark-bg transition-colors flex items-center justify-center"
              onClick={() => setShowThemeSelector(!showThemeSelector)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a3 3 0 106 0V4a2 2 0 00-2-2H4zm1 14a1 1 0 100-2 1 1 0 000 2zm5-1.757l4.9-4.9a2 2 0 000-2.828L13.485 5.1a2 2 0 00-2.828 0L10 5.757v8.486zM16 18H9.071l6-6H16a2 2 0 012 2v2a2 2 0 01-2 2z" clipRule="evenodd" />
              </svg>
            </button>
            
            {showThemeSelector && (
              <div className="absolute right-0 mt-2 w-48 bg-dark-surface border border-dark-border rounded-lg shadow-lg z-50">
                <div className="p-2">
                  <h3 className="text-xs font-semibold text-dark-text-secondary uppercase tracking-wider mb-2 px-2">Themes</h3>
                  {Object.entries(themes).map(([key, theme]) => (
                    <button
                      key={key}
                      onClick={() => {
                        setTheme(key);
                        setShowThemeSelector(false);
                      }}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-left transition-colors ${
                        currentTheme === key 
                          ? 'bg-brand-primary/20 text-brand-primary' 
                          : 'text-dark-text-primary hover:bg-dark-bg'
                      }`}
                    >
                      <span className="flex-shrink-0 w-3 h-3 rounded-full" style={{ backgroundColor: theme.colors['brand-primary'] }}></span>
                      {theme.name}
                    </button>
                  ))}
                </div>
              </div>
            )}
            
            {/* Click outside to close theme selector */}
            {showThemeSelector && (
              <div 
                className="fixed inset-0 z-40" 
                onClick={() => setShowThemeSelector(false)}
              ></div>
            )}
          </div>
          
          {/* Notification Button */}
          <button 
            className="relative p-2 rounded-md text-dark-text-secondary hover:bg-dark-bg flex items-center justify-center"
            onClick={onOpenNotifications}
          >
            <BellIcon className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadCount}
              </span>
            )}
          </button>
        </div>
        
        {/* Click outside to close workspace menu */}
        {showWorkspaceMenu && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => {
              setShowWorkspaceMenu(false);
              setShowNewWorkspaceForm(false);
              setNewWorkspaceName('');
            }}
          ></div>
        )}
      </header>
    );
  };

  export default Header;